import { Action, createReducer, on } from '@ngrx/store';
import { IProjectLabelsModel } from 'src/app/validation/models/project-labels/project-labels';
import * as ProjectLabelsActions from './project-labels.actions';

export type State = IProjectLabelsModel;

const initialState: State = {
  labelsInTables: false,
  displayOnlyTableTitles: false,
  groupTabActive: false,
  tables: null,
  entities: null,
  groups: null,
  selected: {
    group: null,
    files: null,
    tables: null
  },
  loaders: {
    tables: false,
    entities: false,
    groups: false
  }
};

export const projectLabelsReducer = createReducer(
  initialState,

  on(ProjectLabelsActions.ShowLabelsInTables, (state, { labelsInTables }): State => {
    return {
      ...state,
      labelsInTables
    };
  }),

  on(ProjectLabelsActions.GetTableTitles, (state): State => {
    return {
      ...state,
      tables: null,
      loaders: {
        ...state.loaders,
        tables: true
      }
    };
  }),

  on(ProjectLabelsActions.GetTableTitlesSuccess, (state, { tables }): State => {
    return {
      ...state,
      tables,
      loaders: {
        ...state.loaders,
        tables: false
      }
    };
  }),

  on(ProjectLabelsActions.GetTableTitlesFailed, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        tables: false
      }
    };
  }),

  on(ProjectLabelsActions.CreateLabel, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.CreateLabelSuccess, (state, { entities }): State => {
    return {
      ...state,
      entities,
      loaders: {
        ...state.loaders,
        entities: false
      }
    };
  }),

  on(ProjectLabelsActions.CreateLabelFailed, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: false
      }
    };
  }),

  on(ProjectLabelsActions.GetProjectLabels, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.GetProjectLabelsSuccess, (state, { entities }): State => {
    return {
      ...state,
      entities,
      loaders: {
        ...state.loaders,
        entities: false
      }
    };
  }),

  on(ProjectLabelsActions.GetProjectLabelsFailed, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.RemoveLabelFromTable, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.RemoveLabelFromTableSuccess, (state, { entities }): State => {
    return {
      ...state,
      entities,
      loaders: {
        ...state.loaders,
        entities: false
      }
    };
  }),

  on(ProjectLabelsActions.RemoveLabelFromTableFailed, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.RemoveLabelFromProject, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.RemoveLabelFromProjectSuccess, (state, { entities }): State => {
    return {
      ...state,
      entities,
      loaders: {
        ...state.loaders,
        entities: false
      }
    };
  }),

  on(ProjectLabelsActions.RemoveLabelFromProjectFailed, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.GetGroups, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        groups: true
      }
    };
  }),

  on(ProjectLabelsActions.GetGroupsSuccess, (state, { groups }): State => {
    return {
      ...state,
      groups,
      loaders: {
        ...state.loaders,
        groups: false
      }
    };
  }),

  on(ProjectLabelsActions.GetGroupsFailed, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        groups: false
      }
    };
  }),

  on(ProjectLabelsActions.ClearLabelingData, (): State => {
    return {
      ...initialState
    };
  }),

  on(ProjectLabelsActions.GetGroupTablesSuccess, (state, { groupName, fileID, tables }): State => {
    const groups = [...state.groups];
    const group = groups.find(({ name }) => name === groupName);
    const file = group.files.find(({ id }) => id === fileID);
    file.tables = tables;
    return {
      ...state,
      groups
    };
  }),

  on(ProjectLabelsActions.UpdateFiles, (state, { files }): State => {
    const groups = state.groups?.map((group) => {
      const allFiles = group.files.map((file) => {
        const matchingFile = files.find(({ id }) => id === file.id);
        if (!matchingFile) return file;
        const { discrepanciesCounter, fileStatus } = matchingFile;
        return { ...file, discrepanciesCounter, fileStatus };
      });
      return { ...group, files: allFiles };
    });
    return { ...state, groups };
  }),

  on(ProjectLabelsActions.ToggleGroup, (state, { name }): State => {
    const groups = [...state.groups]?.map((group) => {
      if (group.name === name) {
        return { ...group, expanded: !group.expanded };
      }
      return group;
    });
    return { ...state, groups };
  }),

  on(ProjectLabelsActions.SelectGroup, (state, { name }): State => {
    if (!name) {
      return { ...state, selected: { ...state.selected, group: null, tables: null, files: null } };
    }
    const group = state.groups.find((group) => group.name === name);
    const tables = group.files.flatMap((file) => file.tables.map(({ id }) => id));
    const files = group.files.map((file) => file.id);
    return { ...state, selected: { group: name, files, tables } };
  }),

  on(ProjectLabelsActions.SelectTable, (state, { tableID }): State => {
    if (!state.selected.tables) {
      return { ...state, selected: { ...state.selected, tables: [tableID] } };
    }
    const tables = !state.selected.tables?.includes(tableID)
      ? [...state.selected.tables, tableID]
      : state.selected.tables.filter((id) => id !== tableID);
    return { ...state, selected: { ...state.selected, tables } };
  }),

  on(ProjectLabelsActions.SelectFile, (state, { fileID }): State => {
    if (!state.selected.files) {
      return { ...state, selected: { ...state.selected, files: [fileID] } };
    }
    const files = !state.selected.files?.includes(fileID)
      ? [...state.selected.files, fileID]
      : state.selected.files.filter((id) => id !== fileID);
    return { ...state, selected: { ...state.selected, files } };
  }),

  on(ProjectLabelsActions.UnselectAllGroups, (state): State => {
    if (!state.groups) return state;
    const groups = state.groups.map((group) => ({ ...group, selected: false }));
    return { ...state, groups };
  }),

  on(ProjectLabelsActions.DeleteAllLabels, (state): State => {
    return {
      ...state,
      loaders: {
        ...state.loaders,
        groups: true,
        entities: true
      }
    };
  }),

  on(ProjectLabelsActions.DeleteAllLabelsSuccess, (state, { entities }): State => {
    return {
      ...state,
      entities,
      groups: null,
      loaders: {
        ...state.loaders,
        groups: false,
        entities: false
      }
    };
  }),

  on(ProjectLabelsActions.SetGroupTab, (state, { active: groupTabActive }): State => {
    return { ...state, groupTabActive };
  })
);

export const reducer = (state: State, action: Action): State => projectLabelsReducer(state, action);
